/**
 * A club can format its own venue's Long Description in the console with a
 * small Markdown subset — `**bold**`, `*italic*` and `- ` bullets — which only
 * the club app renders (`members-club-platform/mobile/lib/markdown-text.ts`).
 * This widget shows the same column as plain text, so it strips that markup
 * and keeps the words. It reads exactly what the club app reads, and nothing
 * else: an unclosed marker, a marker with a space inside it (`5 * 3 * 2`) or a
 * dash with no space after it stays as typed.
 *
 * @module lib/venueDescription
 */

const BULLET = /^\s*-\s+/;
// Leftmost-first: `**bold**` (no edge space inside; its closing `**` is the
// last of a run of stars) or `*italic*` (no `*` and no edge space inside).
const EMPHASIS = /\*\*(\S(?:[^\n]*?\S)??)\*\*(?!\*)|\*([^*\s](?:[^*\n]*?[^*\s])??)\*/g;
const ITALIC = /\*([^*\s](?:[^*\n]*?[^*\s])??)\*/g;

/**
 * Strips one line's bullet marker and bold / italic markers.
 * @param line - One line of the description
 * @returns The line as plain text
 */
function stripLine(line: string): string {
  return line
    .replace(BULLET, '')
    .replace(EMPHASIS, (_whole, bold: string | undefined, italic: string | undefined) =>
      bold === undefined ? (italic ?? '') : bold.replace(ITALIC, '$1')
    );
}

/**
 * The description as plain text, its line breaks kept.
 * @param description - The venue's `long_description` as stored
 * @returns The same words without the club app's formatting markers
 * @example stripVenueDescriptionMarkup('Rooms:\n- **The Oak Room**') // 'Rooms:\nThe Oak Room'
 */
export function stripVenueDescriptionMarkup(description: string): string {
  return description.split('\n').map(stripLine).join('\n');
}
